// ============================================================================
// WhatsNewModal.jsx — Shows the latest Zenna updates
// Paginated carousel with dots + arrow navigation
// Appears once per release via localStorage version flag
// ============================================================================

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

// Bump this version string on every significant release.
export const CURRENT_RELEASE = '2026-04-20'

const FEATURES = [
  {
    icon: '🛒',
    title: 'Zenna Marketplace',
    body: 'Sell your patterns and designs to other crafters! List digital downloads or physical goods, set your own prices, and get paid through Stripe Connect. Browse and buy designs from the community. Full seller dashboard with revenue analytics, order management, and payout tracking.',
    color: 'from-emerald-500 to-green-600',
  },
  {
    icon: '🧠',
    title: 'Smarter Craft Knowledge',
    body: 'Zenna now has deep knowledge for 45+ craft types — from macrame knot definitions to candle dipping temperatures, polymer clay baking times, and real Perler bead colors. Every pattern includes accurate, verified instructions a beginner can follow.',
    color: 'from-purple-500 to-indigo-600',
  },
  {
    icon: '✂️',
    title: 'Sewing Patterns + Custom Fit',
    body: '65+ garment types, XS-5XL sizing, or upload a full-body photo for custom measurements. Clothing, bags, home decor, baby items, and pet accessories — with complete numbered construction steps.',
    color: 'from-pink-500 to-rose-600',
  },
  {
    icon: '🧵',
    title: 'Stitch Count Validator',
    body: "Knitting and crochet patterns now pass through a built-in stitch count validator. If a row's math doesn't add up, Zenna catches it before you do. No more frogging because row 5 was one stitch short.",
    color: 'from-rose-500 to-pink-600',
  },
  {
    icon: '🕯️',
    title: 'Candle Making Expertise',
    body: 'Container, pillar, layered, cut & carve, taper dipping, and botanical candles — with real techniques, proper cooling times, wick sizing charts, and additive guides (Vybar, UV inhibitor). Plus safety warnings from the National Candle Association.',
    color: 'from-amber-500 to-orange-600',
  },
  {
    icon: '🔄',
    title: 'Craft Conversion',
    body: "Love a crochet pattern but you're a knitter? Ask Zenna to convert it. She translates stitch equivalents, adjusts gauge, swaps tools, and writes the full converted pattern row-by-row.",
    color: 'from-sky-500 to-blue-600',
  },
  {
    icon: '🎨',
    title: 'New Craft Types',
    body: 'Kintsugi, kumihimo, alcohol ink art, diamond painting, rug tufting, needle felting, quilting, latch hook, stained glass, mosaic, zentangle, tie-dye, soap making, jewelry making, and more — all with real materials, techniques, and safety info.',
    color: 'from-violet-500 to-purple-600',
  },
  {
    icon: '🖼️',
    title: 'Better Image Generation',
    body: "Smarter AI routing means better images for tricky crafts. When you say \"I don't like that,\" Zenna modifies the existing design instead of starting over. Plus text-free generation and brand name scrubbing.",
    color: 'from-cyan-500 to-teal-600',
  },
  {
    icon: '🔧',
    title: 'Machine Troubleshooting',
    body: 'Sewing machine tension issues? Serger threading? Embroidery bird-nesting? Cricut blade tearing vinyl? Zenna walks you through diagnostics for sewing machines, sergers, embroidery machines, coverstitch machines, heat presses, and cutting machines.',
    color: 'from-slate-500 to-gray-600',
  },
  {
    icon: '👥',
    title: 'Referral Program',
    body: 'Share Zenna with friends and earn bonus credits when they sign up for a paid plan. Your unique referral code is in your dashboard. Both you and your friend get rewarded!',
    color: 'from-indigo-500 to-violet-600',
  },
  {
    icon: '🌟',
    title: 'Gallery + Remix + Challenges',
    body: "Browse the community gallery, search by craft type, and hit Remix to start from any design you love. Monthly design challenges with community spotlight features. Your best work could be featured on the homepage!",
    color: 'from-yellow-500 to-amber-600',
  },
  {
    icon: '📌',
    title: 'Social Media Tools',
    body: 'Auto-posting to Pinterest and Facebook with platform-optimized captions. Social sharing buttons on every design. UTM tracking for all links. Plus a social media caption generator for your own posts.',
    color: 'from-red-500 to-rose-600',
  },
  {
    icon: '⏱️',
    title: 'Smart Time Estimates',
    body: "Pattern time estimates are now craft-specific. A rainbow loom charm says 1-2 hours, not 8-16. A crochet vest says 15-30 hours. Cross-stitch estimates factor in actual stitch count.",
    color: 'from-green-500 to-emerald-600',
  },
]

export default function WhatsNewModal({ forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    if (forceOpen) {
      setOpen(true)
      setPage(0)
      return
    }
    try {
      const lastSeen = localStorage.getItem('zenna_whats_new_version')
      if (lastSeen !== CURRENT_RELEASE) {
        const timer = setTimeout(() => setOpen(true), 900)
        return () => clearTimeout(timer)
      }
    } catch (e) { /* ignore */ }
  }, [forceOpen])

  const close = () => {
    try {
      localStorage.setItem('zenna_whats_new_version', CURRENT_RELEASE)
    } catch (e) { /* ignore */ }
    setOpen(false)
    if (onClose) onClose()
  }

  if (!open) return null

  const feature = FEATURES[page]
  const isFirst = page === 0
  const isLast = page === FEATURES.length - 1

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with gradient — changes per feature */}
        <div className={`relative bg-gradient-to-br ${feature.color} px-6 py-5 text-white transition-all duration-300`}>
          <button
            onClick={close}
            className="absolute top-3 right-3 text-white/70 hover:text-white w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
          >
            <X size={18} />
          </button>
          <div className="text-[10px] font-bold uppercase tracking-wider text-white/80 mb-1">
            What's New in Zenna
          </div>
          <div className="text-5xl mb-2">{feature.icon}</div>
          <h2 className="text-xl font-bold leading-tight">{feature.title}</h2>
        </div>

        {/* Feature body */}
        <div className="p-6 min-h-[120px]">
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {feature.body}
          </p>
        </div>

        {/* Navigation: arrows + dots */}
        <div className="px-6 pb-2 flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={isFirst}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
              isFirst
                ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-800 dark:hover:text-white'
            }`}
          >
            <ChevronLeft size={20} />
          </button>

          {/* Dots */}
          <div className="flex items-center gap-1.5">
            {FEATURES.map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`rounded-full transition-all duration-200 ${
                  i === page
                    ? 'w-6 h-2 bg-gradient-to-r ' + feature.color
                    : 'w-2 h-2 bg-slate-300 dark:bg-slate-600 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => setPage(p => Math.min(FEATURES.length - 1, p + 1))}
            disabled={isLast}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
              isLast
                ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-800 dark:hover:text-white'
            }`}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {page + 1} of {FEATURES.length}
          </p>
          <div className="flex items-center gap-2">
            {isLast ? (
              <button
                onClick={() => { close(); navigate('/workspace') }}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium hover:opacity-90 transition"
              >
                Let's go!
              </button>
            ) : (
              <button
                onClick={() => setPage(p => p + 1)}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium hover:opacity-90 transition"
              >
                Next
              </button>
            )}
            <button
              onClick={close}
              className="px-4 py-2 rounded-xl text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
